import { useEffect, useState } from "react";
import { ShieldCheck, Save, PlugZap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Field } from "./Shared";
import { api, fmtDate, formatApiError } from "../lib/api";

const EMPTY = { provider: "b2brouter", environment: "sandbox", account_id: "", api_key: "", client_id: "", client_secret: "", customer_id: "", iopole_api_url: "", iopole_token_url: "", auto_send: false };

const Secret = ({ label, has, value, onChange, testid }) => (
  <Field label={label}>
    <Input type="password" autoComplete="new-password" data-testid={testid} value={value} onChange={onChange} placeholder={has ? "•••••••• (enregistrée — laisser vide pour conserver)" : ""} />
  </Field>
);

export default function EInvoicingSettings() {
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({});
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/einvoicing/config").then((r) => {
      setMeta(r.data);
      if (r.data.configured) setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(r.data).filter(([k, v]) => k in EMPTY && v != null)) }));
    });
  }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    try {
      const { data } = await api.put("/einvoicing/config", form);
      setMeta(data);
      setForm((f) => ({ ...f, api_key: "", client_secret: "" }));
      toast.success("Connexion à la Plateforme Agréée enregistrée");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/einvoicing/test");
      setMeta((m) => ({ ...m, last_test: data }));
      toast.success("Connexion réussie");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const b2b = form.provider === "b2brouter";
  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-testid="einvoicing-settings">
      <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-teal-600" /><h2 className="text-lg font-semibold text-slate-900">Facturation électronique — Plateforme Agréée</h2></div>
      <p className="mt-1 text-xs text-slate-500">Transmission des factures Factur-X à votre PA immatriculée DGFiP (réforme 2026-2027). Vos identifiants sont chiffrés et ne sont jamais renvoyés au navigateur.</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <Field label="Plateforme">
          <Select value={form.provider} onValueChange={set("provider")}>
            <SelectTrigger data-testid="pa-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="b2brouter">B2Brouter</SelectItem><SelectItem value="iopole">Iopole</SelectItem></SelectContent>
          </Select>
        </Field>
        <Field label="Environnement">
          <Select value={form.environment} onValueChange={set("environment")}>
            <SelectTrigger data-testid="pa-environment-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="sandbox">Bac à sable (tests)</SelectItem><SelectItem value="production">Production</SelectItem></SelectContent>
          </Select>
        </Field>
        {b2b ? (
          <>
            <Field label="Identifiant de compte (account_id)"><Input data-testid="pa-account-id-input" value={form.account_id} onChange={set("account_id")} /></Field>
            <Secret label="Clé API (X-B2B-API-Key)" has={meta.has_api_key} value={form.api_key} onChange={set("api_key")} testid="pa-api-key-input" />
          </>
        ) : (
          <>
            <Field label="client_id"><Input data-testid="pa-client-id-input" value={form.client_id} onChange={set("client_id")} /></Field>
            <Secret label="client_secret" has={meta.has_client_secret} value={form.client_secret} onChange={set("client_secret")} testid="pa-client-secret-input" />
            <Field label="customer_id"><Input data-testid="pa-customer-id-input" value={form.customer_id} onChange={set("customer_id")} /></Field>
            {form.environment === "production" && (
              <>
                <Field label="URL API production (fournie par Iopole)"><Input data-testid="pa-iopole-api-url-input" value={form.iopole_api_url} onChange={set("iopole_api_url")} placeholder="https://" /></Field>
                <Field label="URL d'authentification production"><Input data-testid="pa-iopole-token-url-input" value={form.iopole_token_url} onChange={set("iopole_token_url")} placeholder="https://" /></Field>
              </>
            )}
          </>
        )}
        <label className="flex items-center gap-2 text-sm sm:col-span-2"><Switch data-testid="pa-auto-send-switch" checked={form.auto_send} onCheckedChange={set("auto_send")} />Transmettre automatiquement chaque nouvelle facture B2B à la PA</label>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button data-testid="pa-save-button" className="bg-[#0b2c6b] hover:bg-[#1559d6]" onClick={save}><Save className="mr-2 h-4 w-4" />Enregistrer</Button>
        <Button data-testid="pa-test-button" variant="outline" disabled={!meta.configured || testing} onClick={test}>{testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}Tester la connexion</Button>
        {meta.last_test?.ok && <span className="text-xs text-teal-700" data-testid="pa-last-test">Connexion validée le {fmtDate(meta.last_test.tested_at)}</span>}
      </div>
    </div>
  );
}
